import { useMemo } from 'react';

import {
  type CustomRoleTaxonomySelection,
  TaxonomyType,
} from '@revelio/data-access';

import { RICS_TAXONOMY_ID } from '../../../../../../shared/selection-lists/constants';
import { useTaxonomyTree } from '../../../../../../shared/selection-lists/hooks/useTaxonomyTree';
import { getRoleTaxonomyId } from '../../../../../../shared/selection-lists/utils/roleTaxonomy';
import type { GeographyMode } from '../../../../../../shared/user-preferences/types';
import { useFilterContext } from '../../../../filters/contexts/FilterProvider';
import {
  type Filter,
  FilterId,
  FilterType,
  type SingleValueFilter,
} from '../../../../filters/types/filters';
import { RICS_TAXONOMY_TREE_LEVELS } from '../../../../filters/utils/ricsTaxonomyTree';
import type { CustomSkillTreeNode } from '../../../../filters/utils/skillTaxonomyTree';
import {
  GEOGRAPHY_TAXONOMY_ID_BY_MODE,
  GEOGRAPHY_TAXONOMY_TREE_LEVELS,
  ROLE_TAXONOMY_TREE_LEVELS,
} from '../../../../filters/utils/taxonomy-tree-levels';
import type { TreemapDrilldownSegment } from '../../../../filters/utils/treemapDrilldownLevels';
import {
  buildParentLookupFromTree,
  type NestableTreeNode,
  type NestedTreemapParent,
} from '../../../Chart/utils/buildNestedTreemapData';

export type { NestedTreemapParent };

const isSingleValueFilter = <T>(
  f: Filter | undefined,
  id: FilterId
): f is SingleValueFilter<T> =>
  Boolean(f && f.type === FilterType.VALUE && f.id === id);

export type UseNestedTreemapParentLookupResult = {
  parentLookup: Map<string, NestedTreemapParent> | null;
  fetching: boolean;
};

export const useNestedTreemapParentLookup = ({
  segment,
  childDepth,
  geographyMode,
  customSkillTree,
  customSkillTreeFetching = false,
}: {
  segment: TreemapDrilldownSegment;
  childDepth: number | null;
  geographyMode: GeographyMode;
  customSkillTree: CustomSkillTreeNode[] | null;
  customSkillTreeFetching?: boolean;
}): UseNestedTreemapParentLookupResult => {
  const { activeFilterMap } = useFilterContext();
  const active = childDepth !== null;

  const roleTaxonomySelection = (() => {
    const roleTaxonomyFilter = activeFilterMap.get(FilterId.ROLE_TAXONOMY);
    if (
      !isSingleValueFilter<CustomRoleTaxonomySelection>(
        roleTaxonomyFilter,
        FilterId.ROLE_TAXONOMY
      )
    ) {
      return undefined;
    }
    return roleTaxonomyFilter.value;
  })();
  const roleReferenceTaxonomyId = useMemo(
    () => getRoleTaxonomyId(roleTaxonomySelection),
    [roleTaxonomySelection]
  );

  const wantsRole = active && segment === 'role';
  const wantsGeography = active && segment === 'geography';
  const wantsRics = active && segment === 'rics';
  const wantsSkill = active && segment === 'skills';

  const { treeData: roleTreeData, fetching: isRoleTreeFetching } =
    useTaxonomyTree({
      taxonomyType: TaxonomyType.Role,
      taxonomyId: roleReferenceTaxonomyId,
      levels: ROLE_TAXONOMY_TREE_LEVELS,
      pause: !wantsRole || roleReferenceTaxonomyId === undefined,
    });

  const { treeData: geographyTreeData, fetching: isGeographyTreeFetching } =
    useTaxonomyTree({
      taxonomyType: TaxonomyType.Geography,
      taxonomyId: GEOGRAPHY_TAXONOMY_ID_BY_MODE[geographyMode],
      levels: GEOGRAPHY_TAXONOMY_TREE_LEVELS,
      pause: !wantsGeography,
    });

  const { treeData: ricsTreeData, fetching: isRicsTreeFetching } =
    useTaxonomyTree({
      taxonomyType: TaxonomyType.Rics,
      taxonomyId: RICS_TAXONOMY_ID,
      levels: RICS_TAXONOMY_TREE_LEVELS,
      pause: !wantsRics,
    });

  const treeNodes: readonly NestableTreeNode[] =
    ((): readonly NestableTreeNode[] => {
      if (wantsRole) return roleTreeData;
      if (wantsGeography) return geographyTreeData;
      if (wantsRics) return ricsTreeData;
      if (wantsSkill) return customSkillTree ?? [];
      return [];
    })();

  const fetching =
    (wantsRole && isRoleTreeFetching) ||
    (wantsGeography && isGeographyTreeFetching) ||
    (wantsRics && isRicsTreeFetching) ||
    (wantsSkill && customSkillTreeFetching);

  const parentLookup = useMemo(() => {
    if (childDepth === null) return null;
    if (treeNodes.length === 0) return null;
    return buildParentLookupFromTree(treeNodes, childDepth);
  }, [childDepth, treeNodes]);

  return { parentLookup, fetching };
};
